import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  FormFeedback,
} from "reactstrap";
import UserHeader from "components/Headers/UserHeader.js";
import { ToastContainer, toast } from "react-toastify"; // Import Toastify
import "react-toastify/dist/ReactToastify.css"; // Import styles for Toastify
import BonusesList from "components/Bonuseslist/Bonuseslist";

const Bonuses = () => {
  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    min_purchase_amount: "",
    bonus_percentage: "",
    description: "",
    status: "active", // default status
  });

  const [errors, setErrors] = useState({
    start_date: "",
    end_date: "",
    min_purchase_amount: "",
    bonus_percentage: "",
    description: "",
  });

  // Handle changes to the form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Validate the form data before submitting
  const validateForm = () => {
    let formErrors = {};
    let valid = true;

    // Validate start date and end date (start date must be before or equal to end date)
    if (!formData.start_date) {
      formErrors.start_date = "Start Date is required!";
      valid = false;
    }
    if (!formData.end_date) {
      formErrors.end_date = "End Date is required!";
      valid = false;
    } else if (new Date(formData.start_date) > new Date(formData.end_date)) {
      formErrors.end_date = "End Date must be after Start Date!";
      valid = false;
    }

    // Validate minimum purchase amount
    if (
      !formData.min_purchase_amount ||
      isNaN(formData.min_purchase_amount) ||
      parseFloat(formData.min_purchase_amount) <= 0
    ) {
      formErrors.min_purchase_amount =
        "Minimum purchase amount must be a positive number!";
      valid = false;
    }

    // Validate bonus percentage
    if (
      !formData.bonus_percentage ||
      isNaN(formData.bonus_percentage) ||
      parseFloat(formData.bonus_percentage) <= 0
    ) {
      formErrors.bonus_percentage =
        "Bonus percentage must be a positive number!";
      valid = false;
    }

    setErrors(formErrors);
    return valid;
  };

  // Submit the form data
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Prepare the data to be sent to the backend
    const bonusData = {
      start_date: formData.start_date,
      end_date: formData.end_date,
      min_purchase_amount: formData.min_purchase_amount,
      bonus_percentage: formData.bonus_percentage,
      description: formData.description || null, // Description is optional
      status: formData.status,
    };

    try {
      const token = localStorage.getItem("token");

      // Send data to the backend using fetch
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bonuses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bonusData), // Send form data as a JSON string
      });

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }

      const data = await response.json();

      if (response.ok) {
        // Show success toast if the bonus was created successfully
        toast.success("Bonus created successfully!", {
          position: "top-center",
          autoClose: 3000,
        });

        // Log or display the generated bonus code
        console.log("Bonus Code:", data.bonus.bonus_code); // Bonus code received from server

        // Optionally display the bonus code in the UI
        toast.info(`Your unique bonus code is: ${data.bonus.bonus_code}`, {
          position: "top-center",
          autoClose: 3000,
        });
        setTimeout(() => {
          window.location.reload();
        }, 3000);
        // Reset the form data
        setFormData({
          start_date: "",
          end_date: "",
          min_purchase_amount: "",
          bonus_percentage: "",
          description: "",
          status: "active",
        });
      } else {
        // Show error toast if the server responds with an error
        toast.error(
          data.error || "An error occurred while creating the bonus.",
          {
            position: "top-center",
            autoClose: 3000,
          },
        );
      }
    } catch (error) {
      console.error("Error submitting the bonus:", error);
      toast.error("An error occurred while submitting the bonus.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      <UserHeader />
      <Container
        className="mt--7"
        style={{ background: "#251d0c", paddingBottom: "100px" }}
        fluid
      >
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card className="bg-dark">
              <CardBody style={{ background: "black" }}>
                <Form onSubmit={handleSubmit}>
                  <h6
                    className="heading-small text-light mb-4"
                    style={{ fontSize: "1.5rem" }}
                  >
                    Bonus Details
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-start_date"
                          >
                            Start Date
                          </label>
                          <Input
                            name="start_date"
                            value={formData.start_date}
                            onChange={handleChange}
                            type="datetime-local"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                            invalid={!!errors.start_date}
                          />
                          {errors.start_date && (
                            <FormFeedback>{errors.start_date}</FormFeedback>
                          )}
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-end_date"
                          >
                            End Date
                          </label>
                          <Input
                            name="end_date"
                            value={formData.end_date}
                            onChange={handleChange}
                            type="datetime-local"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                            invalid={!!errors.end_date}
                          />
                          {errors.end_date && (
                            <FormFeedback>{errors.end_date}</FormFeedback>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-min_purchase_amount"
                          >
                            Minimum Purchase Amount
                          </label>
                          <Input
                            name="min_purchase_amount"
                            value={formData.min_purchase_amount}
                            onChange={handleChange}
                            min={0}
                            placeholder="100.00"
                            type="number"
                            step="0.01"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                            invalid={!!errors.min_purchase_amount}
                          />
                          {errors.min_purchase_amount && (
                            <FormFeedback>
                              {errors.min_purchase_amount}
                            </FormFeedback>
                          )}
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-bonus_percentage"
                          >
                            Bonus Percentage
                          </label>
                          <Input
                            name="bonus_percentage"
                            value={formData.bonus_percentage}
                            onChange={handleChange}
                            min={0}
                            placeholder="10.00"
                            type="number"
                            step="0.01"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                            invalid={!!errors.bonus_percentage}
                          />
                          {errors.bonus_percentage && (
                            <FormFeedback>
                              {errors.bonus_percentage}
                            </FormFeedback>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-description"
                          >
                            Description (Optional)
                          </label>
                          <Input
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Enter bonus description"
                            type="textarea"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-status"
                          >
                            Status
                          </label>
                          <Input
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            type="select"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Button
                      color="primary"
                      size="lg"
                      style={{
                        backgroundColor: "#FFB000",
                        color: "#222",
                        fontSize: "1.4rem",
                      }}
                    >
                      Create Bonus
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <BonusesList />
      <ToastContainer />
    </>
  );
};

export default Bonuses;
